import React, { useState, useEffect } from 'react';
import api from '../../../services/api';
import { maskBr } from 'js-brasil';
import toast from 'react-hot-toast';
import RestoreConfirmationModal from './modals/RestoreConfirmationModal';
import PermanentDeleteModal from './modals/PermanentDeleteModal';

export default function SuperAdminExclusions() {
  const [deletedCompanies, setDeletedCompanies] = useState([]);
  const [deletedUsers, setDeletedUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false); // Para feedback nas ações
  const [error, setError] = useState(null);
  const [view, setView] = useState('companies'); // 'companies' ou 'users'
  
  // Estado para o novo modal de restauração
  const [isRestoreModalOpen, setIsRestoreModalOpen] = useState(false);
  const [itemToRestore, setItemToRestore] = useState(null);

  // Estado para o modal de exclusão permanente
  const [isPermanentDeleteModalOpen, setIsPermanentDeleteModalOpen] = useState(false);
  const [itemToPermanentlyDelete, setItemToPermanentlyDelete] = useState(null);

  const fetchDeletedItems = async () => {
    try {
      setLoading(true);
      // Busca empresas e usuários excluídos em paralelo
      const [companiesRes, usersRes] = await Promise.all([
        api.get('/companies/deleted'),
        api.get('/users/deleted')
      ]);
      setDeletedCompanies(companiesRes.data);
      setDeletedUsers(usersRes.data);
      setError(null);
    } catch (err) {
      setError('Falha ao carregar itens da lixeira.');
      console.error("Erro ao carregar itens da lixeira:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDeletedItems();
    toast.promise(fetchDeletedItems(), { loading: 'Carregando lixeira...', success: 'Lixeira carregada!', error: 'Falha ao carregar lixeira.' });
  }, []);

  const handleRestoreClick = (item, type) => {
    setItemToRestore({ ...item, type });
    setIsRestoreModalOpen(true);
  };

  const handleCloseRestoreModal = () => {
    setItemToRestore(null);
    setIsRestoreModalOpen(false);
  };

  const handleConfirmRestore = async (item, dependenciesToRestore) => {
    const itemType = itemToRestore.type;
    const endpoint = itemType === 'company' ? 'companies' : 'users';
    const identifier = itemType === 'company' ? item.slug : item.userName;

    try {
      setIsSubmitting(true);
      await api.post(`/${endpoint}/${identifier}/restore`, {
        usersToRestore: dependenciesToRestore?.users,
      });

      if (itemType === 'company') {
        setDeletedCompanies(prev => prev.filter(c => c.id !== item.id));
      } else {
        setDeletedUsers(prev => prev.filter(u => u.id !== item.id));
      }
      handleCloseRestoreModal();
      toast.success(`${itemType === 'company' ? 'Empresa' : 'Usuário'} restaurado(a) com sucesso!`);
    } catch (err) {
      const errorMsg = err.response?.data?.message || 'Erro ao restaurar o item.';
      toast.error(errorMsg);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePermanentDeleteClick = (item, type) => {
    setItemToPermanentlyDelete({ ...item, type });
    setIsPermanentDeleteModalOpen(true);
  };

  const handleClosePermanentDeleteModal = () => {
    setItemToPermanentlyDelete(null);
    setIsPermanentDeleteModalOpen(false);
  };

  const handleConfirmPermanentDelete = async (item) => {
    const itemType = itemToPermanentlyDelete.type;
    const endpoint = itemType === 'company' ? 'companies' : 'users';
    const identifier = itemType === 'company' ? item.slug : item.userName;

    try {
      setIsSubmitting(true);
      await api.delete(`/${endpoint}/${identifier}/permanent`);
      await fetchDeletedItems(); // Recarrega os itens da lixeira
      toast.success('Item excluído permanentemente.');
      handleClosePermanentDeleteModal();
    } catch (err) {
      const errorMsg = err.response?.data?.message || 'Não foi possível excluir permanentemente.';
      toast.error(errorMsg);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) return <div className="loading-state"><div className="loading-spinner"></div> Carregando lixeira...</div>;
  if (error) return <div className="error-message">{error}</div>;

  return (
    <>
      <div className="card">
        <div className="card-body">
          <div className="tabs-container" style={{ marginBottom: 'var(--space-6)' }}>
            <button
              className={`tab-button ${view === 'companies' ? 'active' : ''}`}
              onClick={() => setView('companies')}
            >
              Empresas ({deletedCompanies.length})
            </button>
            <button
              className={`tab-button ${view === 'users' ? 'active' : ''}`}
              onClick={() => setView('users')}
            >
              Usuários ({deletedUsers.length})
            </button>
          </div>

          {view === 'companies' && (
            <div>
              <h2>Empresas na Lixeira</h2>
              {deletedCompanies.length === 0 ? (
                <p>Nenhuma empresa na lixeira.</p>
              ) : (
                <div className="table-wrapper">
                  <table className="users-table">
                    <thead>
                      <tr>
                        <th>Nome</th>
                        <th>CNPJ</th>
                        <th>Data da Exclusão</th>
                        <th style={{ width: '1%' }}>Ações</th>
                      </tr>
                    </thead>
                    <tbody>
                      {deletedCompanies.map(company => (
                        <tr key={company.slug}>
                          <td>{company.name}</td>
                          <td>{company.cnpjOrTaxId ? maskBr.cnpj(company.cnpjOrTaxId) : 'Não informado'}</td>
                          <td>{new Date(company.deletedAt).toLocaleDateString('pt-BR')}</td>
                          <td>
                            <div className="table-actions">
                              <button className="btn btn-sm btn-secondary" onClick={() => handleRestoreClick(company, 'company')}>Restaurar</button>
                              <button className="btn btn-sm btn-danger" onClick={() => handlePermanentDeleteClick(company, 'company')}>Excluir Perm.</button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {view === 'users' && (
            <div>
              <h2>Usuários na Lixeira</h2>
              {deletedUsers.length === 0 ? (
                <p>Nenhum usuário na lixeira.</p>
              ) : (
                <div className="table-wrapper">
                  <table className="users-table">
                    <thead>
                      <tr>
                        <th>Nome</th>
                        <th>Email</th>
                        <th>Empresa</th>
                        <th>Data da Exclusão</th>
                        <th style={{ width: '1%' }}>Ações</th>
                      </tr>
                    </thead>
                    <tbody>
                      {deletedUsers.map(user => (
                        <tr key={user.userName}>
                          <td>{user.fullName}</td>
                          <td>{user.email}</td>
                          <td>{user.company?.name || 'Não informado'}</td>
                          <td>{new Date(user.deletedAt).toLocaleDateString('pt-BR')}</td>
                          <td>
                            <div className="table-actions">
                              <button className="btn btn-sm btn-secondary" onClick={() => handleRestoreClick(user, 'user')}>Restaurar</button>
                              <button className="btn btn-sm btn-danger" onClick={() => handlePermanentDeleteClick(user, 'user')}>Excluir Perm.</button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {isRestoreModalOpen && (
        <RestoreConfirmationModal
          open={isRestoreModalOpen}
          onClose={handleCloseRestoreModal}
          onConfirm={handleConfirmRestore}
          item={itemToRestore}
          itemType={itemToRestore?.type}
          isSubmitting={isSubmitting}
        />
      )}

      {isPermanentDeleteModalOpen && (
        <PermanentDeleteModal
          open={isPermanentDeleteModalOpen}
          onClose={handleClosePermanentDeleteModal}
          onConfirm={handleConfirmPermanentDelete}
          item={itemToPermanentlyDelete}
          itemType={itemToPermanentlyDelete?.type}
          isSubmitting={isSubmitting}
        />
      )}
    </>
  );
}